import ApiClient from '@/api/ApiClient';
import Collection from '@/helpers/Collection';
import UserCourseVideo from '@/api/models/UserCourseVideo';
import store from '@/store';
import BaseService from './BaseService';

class CourseVideoService extends BaseService {
    #prefixUrl = 'api/video/';

    STORAGE_DEFINITION = {
        MODULE_VIDEOS: 'moduleVideos',
    };

    constructor() {
        super();
        this.api = new ApiClient(this.#prefixUrl);
    }

    /**
     * Updates course video status
     * @param {integer} videoId
     * @param {integer} status
     * @return {UserCourseVideo|Null}
     */
    async saveUserCourseVideoStatus(videoId = null, status = 0) {
        const response = await this.api.executeRequest(`/status/${videoId}`, { status: status }, this.api.methods.POST);
        if (!response.success())
            return null;
        const userCourseVideo = new UserCourseVideo(response.getContent());
        store.dispatch('saveOnDataChest', {
            key: `user_course_video_${videoId}`,
            value: userCourseVideo
        });
        return userCourseVideo;
    }

    /**
     * Gets all course video status by logged user
     * @returns {Array}
     */
    async getUserCourseVideoStatus(courseUuid = null) {
        let response = await this.api.executeRequest(`/status/${courseUuid}`, {}, this.api.methods.GET);
        let data = [];
        for (let item of response.getContent()) {
            data.push(new UserCourseVideo(item));
        }
        response.setContent(new Collection(data, 'course_module_video'));
        return response;
    }

    /**
     * Gets Video by module
     * @param {integer} moduleId 
     * @parma {bool} forceReload - default false
     * @returns {Array}
     */
    async getVideoByModule(moduleId = null, forceReload = true) {
        let videosList = forceReload ? [] : this.getArrayOnStorage(this.STORAGE_DEFINITION.MODULE_VIDEOS);
        
        if (videosList.length == 0) {
            const response = await this.api.executeRequest(`/module/${moduleId}`, {}, this.api.methods.GET);
            
            if (response.success()) {
                videosList = response.getContent().map(videoData => videoData);
                this.saveArrayOnStorage(this.STORAGE_DEFINITION.MODULE_VIDEOS, videosList);
            }
        }
        
        return videosList;
    }

    /**
     * Saves video by creating or updating
     * Obs: if uuid is sent, updates video
     * @param {object} videoData
     * @returns {api/models/Response}
     */
    async saveVideo(videoData = {}) {
        let response = false;

        if (videoData.id != null) {
            response = await this.api.executeRequest(`/${videoData.id}`, videoData, this.api.methods.PUT);
        } else {
            response = await this.api.executeRequest('/', videoData, this.api.methods.POST);
        }
        
        return response;
    }

    /**
     * Saves video by creating or updating
     * Obs: if id is sent, updates video
     * @param {object} videoData
     * @returns {api/models/Response}
     */
    async saveVideoFile(videoData = {}, id = null) {
        const response = await this.api.executeRequest(
            `/file/${id}`, 
            videoData, 
            this.api.methods.PUT,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                }
            }
        );

        return response;
    }

    /**
     * Deletes Video
     * @param {integer} id
     * @returns {api/models/Response}
     */
    async deleteVideo(id = null) {
        const response = await this.api.executeRequest(`/${id}`, {}, this.api.methods.DELETE);

        return response;
    }

    /**
     * Checks if video is avaliable for playing
     * @returns {Boolean}
     */
    async isVideoAvaliable(id = null) {
        const response = await this.api.executeRequest(`/avaliable/${id}`, {}, this.api.methods.GET);

        return response.success() ? response.getContent().isAvaliable : false;
    }

    /**
     * Updates Video order
     * @param {object} videoData
     * @returns {api/models/Response}
     */
    async updateVideoOrder(videoData = {}) {
        return await this.api.executeRequest(`/order`, videoData, this.api.methods.POST);
    }

    /**
     * Gets Video source files for playing on Video
     * @param {string|null} videoId 
     * @returns 
     */
    async getVideoSource(videoId = null) {
        return await this.api.executeRequest(`/${videoId}/play`, {}, this.api.methods.GET);
    }

    /**
     * Gets video fragment url
     * @param {string|null} videoId 
     * @returns 
     */
    getVideoFragmentUrl(videoId = null) {
        return `${process.env.VUE_APP_API_URL}api/video/${videoId}/play`;
    }
}

export default new CourseVideoService();
